/** AT02 copy (one group per control; a ticket edits only its own file). */
export const at02PtBr = {
  title: 'Disponibilidade para uso, vida útil e início da depreciação',
  description:
    'Bens e projetos sem data de disponibilidade, vida útil, valor residual ou início de depreciação coerentes.',
  prerequisite:
    'Requer a leitura das datas de entrada em uso, das estimativas de vida útil e valor residual e da regra do livro.',
  reasons: {
    BOOK_RULE_MISSING: 'Bem em uso sem regra de depreciação no livro',
    IN_SERVICE_DATE_MISSING: 'Bem em uso sem data de início de uso',
    IN_SERVICE_BEFORE_ACQUISITION: 'Início de uso anterior à aquisição',
    DEPRECIATION_STARTS_BEFORE_SERVICE: 'Depreciação começa antes do início de uso',
    DEPRECIATION_STARTS_AFTER_SERVICE: 'Depreciação começa depois do início de uso',
    USEFUL_LIFE_INVALID: 'Vida útil ausente ou inválida',
    RESIDUAL_RATE_INVALID: 'Valor residual inválido',
    ROU_WITHOUT_LEASE_CONTRACT: 'Direito de uso sem contrato de arrendamento',
    ROU_LIFE_EXCEEDS_LEASE_TERM: 'Direito de uso amortizado além do fim do contrato',
    INDEFINITE_LIFE_WITH_SCHEDULE: 'Vida útil indefinida com cronograma de depreciação',
    INDEFINITE_LIFE_WITH_DEPRECIATION_METHOD: 'Vida útil indefinida com método de depreciação',
    DRAFT_WITH_LIVE_SCHEDULE: 'Bem em rascunho com cronograma ativo',
    DEPRECIABLE_ASSET_WITHOUT_METHOD: 'Bem sem método de depreciação em uma classe que deprecia',
    ESTIMATE_CHANGE_AWAITING_REVIEW: 'Mudança de estimativa aguardando revisão',
  },
  wayOut: {
    BOOK_RULE_MISSING: 'Cadastre a regra do livro (método, vida útil e valor residual) no bem.',
    IN_SERVICE_DATE_MISSING: 'Informe a data em que o bem ficou disponível para uso.',
    IN_SERVICE_BEFORE_ACQUISITION: 'Corrija a data de início de uso ou a data de aquisição do bem.',
    DEPRECIATION_STARTS_BEFORE_SERVICE:
      'Ajuste o início da depreciação para o mês em que o bem ficou disponível para uso.',
    DEPRECIATION_STARTS_AFTER_SERVICE:
      'Ajuste o início da depreciação para o mês de entrada em uso. Se o bem veio da migração, o início é o mês seguinte à linha de abertura; sem ela, registre a posição de abertura.',
    USEFUL_LIFE_INVALID:
      'Defina a vida útil estimada do bem. O sistema não a assume a partir da classe.',
    RESIDUAL_RATE_INVALID: 'Informe um valor residual de 0% até menos de 100% do custo.',
    ROU_WITHOUT_LEASE_CONTRACT: 'Vincule o bem ao contrato de arrendamento que o originou.',
    ROU_LIFE_EXCEEDS_LEASE_TERM:
      'Revise o início ou a vida útil: o direito de uso é amortizado até o fim do contrato, e não depois dele.',
    INDEFINITE_LIFE_WITH_SCHEDULE:
      'Cancele o cronograma ou revise a classe do bem: ativos de vida útil indefinida não se depreciam.',
    INDEFINITE_LIFE_WITH_DEPRECIATION_METHOD:
      'Troque o método do livro para sem depreciação ou revise a classe do bem.',
    DRAFT_WITH_LIVE_SCHEDULE: 'Cancele o cronograma ou conclua a capitalização do bem.',
    DEPRECIABLE_ASSET_WITHOUT_METHOD:
      'Defina o método e a vida útil na regra do livro do bem, ou ajuste a regra da classe se ela não deve depreciar.',
    ESTIMATE_CHANGE_AWAITING_REVIEW:
      'Revise a nova vida útil ou o novo valor residual e registre a revisão deste apontamento.',
  },
  observations: {
    AT02_SCOPE_UNRESOLVED: 'Moeda funcional ou período do livro não resolvidos',
    AT02_LEASE_TREATMENT_UNRESOLVED: 'Direitos de uso sem tratamento do contrato neste livro',
    AT02_LEASE_BOOK_NOT_RECOGNIZED:
      'Direitos de uso que este livro não reconhece (pagamentos em despesa)',
    AT02_IN_SERVICE_AFTER_CUTOFF: 'Bens que entram em uso depois do fim do período',
    AT02_NOT_MEASURABLE_AT_CUTOFF:
      'Bens baixados, reclassificados ou revisados depois do fim do período: não medidos nesta data. Reavalie no período em que a mudança ocorreu.',
    AT02_EARLIER_REVISION_UNREVIEWED:
      'Revisões de estimativa de períodos anteriores sem revisão aceita. Avalie o período da revisão e registre a revisão do apontamento.',
  },
  asset: 'Bem {{code}}',
  openAsset: 'Abrir bem',
  listTruncated: 'Mostrando {{shown}} de {{total}} apontamentos.',
};

export const at02En: typeof at02PtBr = {
  title: 'Availability for use, useful life and depreciation start',
  description:
    'Assets and projects missing a consistent date available for use, useful life, residual value or depreciation start.',
  prerequisite:
    'Needs in-service dates, useful life and residual value estimates, and the book rule.',
  reasons: {
    BOOK_RULE_MISSING: 'Asset in service with no depreciation rule on the book',
    IN_SERVICE_DATE_MISSING: 'Asset in service with no in-service date',
    IN_SERVICE_BEFORE_ACQUISITION: 'In-service date before acquisition',
    DEPRECIATION_STARTS_BEFORE_SERVICE: 'Depreciation starts before the asset is in service',
    DEPRECIATION_STARTS_AFTER_SERVICE: 'Depreciation starts after the asset is in service',
    USEFUL_LIFE_INVALID: 'Useful life missing or invalid',
    RESIDUAL_RATE_INVALID: 'Residual value invalid',
    ROU_WITHOUT_LEASE_CONTRACT: 'Right of use with no lease contract',
    ROU_LIFE_EXCEEDS_LEASE_TERM: 'Right of use amortized past the end of the lease',
    INDEFINITE_LIFE_WITH_SCHEDULE: 'Indefinite life with a depreciation schedule',
    INDEFINITE_LIFE_WITH_DEPRECIATION_METHOD: 'Indefinite life with a depreciation method',
    DRAFT_WITH_LIVE_SCHEDULE: 'Draft asset with a live schedule',
    DEPRECIABLE_ASSET_WITHOUT_METHOD:
      'Asset with no depreciation method in a class that depreciates',
    ESTIMATE_CHANGE_AWAITING_REVIEW: 'Estimate change awaiting review',
  },
  wayOut: {
    BOOK_RULE_MISSING: 'Add the book rule (method, useful life and residual value) to the asset.',
    IN_SERVICE_DATE_MISSING: 'Enter the date the asset became available for use.',
    IN_SERVICE_BEFORE_ACQUISITION: 'Correct the in-service date or the acquisition date.',
    DEPRECIATION_STARTS_BEFORE_SERVICE:
      'Move the depreciation start to the month the asset became available for use.',
    DEPRECIATION_STARTS_AFTER_SERVICE:
      'Move the depreciation start to the in-service month. If the asset came from the migration, the start is the month after the opening row; without one, record the opening position.',
    USEFUL_LIFE_INVALID:
      'Set the estimated useful life of the asset. The system does not take it from the class.',
    RESIDUAL_RATE_INVALID: 'Enter a residual value from 0% to under 100% of cost.',
    ROU_WITHOUT_LEASE_CONTRACT: 'Link the asset to the lease contract it came from.',
    ROU_LIFE_EXCEEDS_LEASE_TERM:
      'Review the start or the useful life: the right of use is amortized to the end of the lease, not past it.',
    INDEFINITE_LIFE_WITH_SCHEDULE:
      'Cancel the schedule or review the asset class: indefinite-life assets are not depreciated.',
    INDEFINITE_LIFE_WITH_DEPRECIATION_METHOD:
      'Change the book method to no depreciation or review the asset class.',
    DRAFT_WITH_LIVE_SCHEDULE: 'Cancel the schedule or finish capitalizing the asset.',
    DEPRECIABLE_ASSET_WITHOUT_METHOD:
      'Set the method and useful life on the asset book rule, or change the class rule if it should not depreciate.',
    ESTIMATE_CHANGE_AWAITING_REVIEW:
      'Review the new useful life or residual value and record the review of this finding.',
  },
  observations: {
    AT02_SCOPE_UNRESOLVED: 'Book functional currency or period not resolved',
    AT02_LEASE_TREATMENT_UNRESOLVED: 'Rights of use with no contract treatment on this book',
    AT02_LEASE_BOOK_NOT_RECOGNIZED:
      'Rights of use this book does not recognize (payments expensed)',
    AT02_IN_SERVICE_AFTER_CUTOFF: 'Assets that enter service after the period end',
    AT02_NOT_MEASURABLE_AT_CUTOFF:
      'Assets disposed of, reclassified or revised after the period end: not measured at this date. Re-evaluate the period in which the change happened.',
    AT02_EARLIER_REVISION_UNREVIEWED:
      'Estimate revisions from earlier periods with no accepted review. Evaluate the revision period and record the review of the finding.',
  },
  asset: 'Asset {{code}}',
  openAsset: 'Open asset',
  listTruncated: 'Showing {{shown}} of {{total}} findings.',
};
